import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';

import { Button } from 'ui-components/components/button';
import { IconButton } from 'ui-components/components/button/icon-button';
import { Divider } from 'ui-components/components/divider';
import { Drawer } from 'ui-components/components/drawer/drawer';
import { DrawerHeader } from 'ui-components/components/drawer/drawer-header';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import Global from '../../../../store/global-selectors';
import { trackShipmentRequest } from '../../../../store/ltl/ltl.actions';
import type { LtlShipment } from '@shipstation/client-common/types/ltl';
import { CostCard } from './components/booking-confirmation/cards/cost-card';
import { DocumentsCard } from './components/booking-confirmation/cards/documents-card';
import { HandlingUnitsCard } from './components/booking-confirmation/cards/handling-units-card';
import { ShipmentDetailsCard } from './components/booking-confirmation/cards/shipment-details-card';
import { TrackingCard } from './components/booking-confirmation/cards/tracking-card';
import { WizardStep as WizardStepComponent } from './components/wizard-step/wizard-step';
import { ShipFromStep, ShipFromStepSummary } from './components/ship-from-step/ship-from-step';
import { ShipToStep, ShipToStepSummary } from './components/ship-to-step/ship-to-step';
import { HandlingUnitsStep, HandlingUnitsStepSummary } from './components/handling-units-step/handling-units-step';
import { QuotesStep, QuotesStepSummary } from './components/quotes-step/quotes-step';
import { AdditionalDetailsStep, AdditionalDetailsStepSummary } from './components/additional-details-step/additional-details-step';
import { ShipmentSummary } from './components/shipment-summary/shipment-summary';
import { FreightConfigForm } from './freight-config-form/freight-config-form';
import { FreightQuoteResults } from './components/freight-quote-results/freight-quote-results';
import { FreightQuoteProvider, useFreightQuote } from './freight-quote-context';
import type { FreightQuoteSeedData, WizardStep as WizardStepType } from './freight-quote-context';
import { freightQuoteModalMessages } from './messages';

import style from './freight-quote-modal.module.less';

type FreightQuoteModalProps = {
  onClose: () => void;
  wizardOpenConfig?: import('../../../../store/ltl/ltl.types').WizardOpenConfig | null;
  seed?: FreightQuoteSeedData | null;
};

const BOOKING_CONFIRMATION_VIEW = 'booking-confirmation';

type ConfirmationNavBarProps = {
  onClose: () => void;
};

const ConfirmationNavBar = ({ onClose }: ConfirmationNavBarProps) => {
  const dispatch = useDispatch();
  const { state: wizardState } = useFreightQuote();
  const viewingShipmentId = wizardState.viewingShipmentId;
  const shipment = useSelector((state) =>
    viewingShipmentId ? Global.selectEntitiesFreightShipmentById(state, { freightShipmentId: viewingShipmentId }) : null,
  ) as LtlShipment | null;

  const handleTrackShipment = useCallback(() => {
    if (!shipment) return;
    const trackingNumber = shipment.proNumber ?? shipment.bolNumber ?? '';
    const trackingType = shipment.proNumber ? 'PRO' : 'BOL';
    dispatch(trackShipmentRequest(trackingNumber, trackingType, shipment.carrierScac ?? undefined));
  }, [dispatch, shipment]);

  const hasTrackingNumber = Boolean(shipment?.proNumber ?? shipment?.bolNumber);

  return (
    <FlexRow className={style['confirmation-nav-bar']} gap={Gap.Space16}>
      <FlexRow className={style['confirmation-nav-bar-info']} gap={Gap.Space8}>
        {shipment?.carrierName && (
          <Text className={style['confirmation-nav-bar-text']} isBold textType={TextType.H4} value={shipment.carrierName} />
        )}
        {shipment?.bolNumber && <Text className={style['confirmation-nav-bar-text']} textType={TextType.H4} value={shipment.bolNumber} />}
      </FlexRow>
      <FlexRow className={style['confirmation-nav-bar-actions']} gap={Gap.Space12}>
        <Button disabled={!hasTrackingNumber} onClick={handleTrackShipment} theme={THEME_CONSTANTS.tertiary}>
          <FormattedMessage {...freightQuoteModalMessages.trackButton} />
        </Button>
        <Divider className={style['confirmation-nav-bar-divider']} orientation='vertical' />
        <IconButton ariaLabelMessageDescriptor={freightQuoteModalMessages.closeConfirmationAriaLabel} icon={faTimes} onClick={onClose} />
      </FlexRow>
    </FlexRow>
  );
};

const ConfirmationBody = () => {
  const { state: wizardState } = useFreightQuote();
  const viewingShipmentId = wizardState.viewingShipmentId;
  const shipmentDetails = useSelector((state) =>
    viewingShipmentId ? Global.selectEntitiesFreightShipmentById(state, { freightShipmentId: viewingShipmentId }) : null,
  ) as LtlShipment | null;
  const bookingConfirmation = wizardState.bookingConfirmation;

  if (!shipmentDetails) return null;

  return (
    <div className={style['confirmation-body']}>
      <div className={style['confirmation-grid']}>
        <FlexColumn gap={Gap.Space16}>
          <ShipmentDetailsCard shipmentDetails={shipmentDetails} />
          <HandlingUnitsCard shipmentDetails={shipmentDetails} />
        </FlexColumn>
        <FlexColumn gap={Gap.Space16}>
          <TrackingCard bookingConfirmation={bookingConfirmation} shipmentDetails={shipmentDetails} />
          <DocumentsCard shipmentDetails={shipmentDetails} />
          <CostCard shipmentDetails={shipmentDetails} />
        </FlexColumn>
      </div>
    </div>
  );
};

const BOOKING_WIZARD_VIEW = 'booking-wizard';

const STEP_ORDER: WizardStepType[] = ['ship-from', 'ship-to', 'handling-units', 'quotes', 'additional-details'];

const getNextStep = (current: WizardStepType): WizardStepType | null => {
  const idx = STEP_ORDER.indexOf(current);
  return idx < STEP_ORDER.length - 1 ? STEP_ORDER[idx + 1] : null;
};

const FreightQuoteModalInner = ({ onClose }: { onClose: () => void }) => {
  const { state, localDispatch, getQuotes, bookShipment, saveQuote } = useFreightQuote();
  const drawerView = state.drawerView;
  const activeStep = state.activeStep;
  const completedSteps = state.completedSteps;
  const isBookingWizard = drawerView === BOOKING_WIZARD_VIEW;

  const handleCompleteStep = useCallback(
    (step: WizardStepType) => {
      localDispatch({ type: 'COMPLETE_STEP', step });
      const next = getNextStep(step);
      if (next) {
        localDispatch({ type: 'SET_ACTIVE_STEP', step: next });
      }
    },
    [localDispatch],
  );

  const handleEditStep = useCallback(
    (step: WizardStepType) => {
      localDispatch({ type: 'SET_ACTIVE_STEP', step });
    },
    [localDispatch],
  );

  const handleShipFromSave = useCallback(() => {
    handleCompleteStep('ship-from');
  }, [handleCompleteStep]);

  const handleShipToSave = useCallback(() => {
    handleCompleteStep('ship-to');
  }, [handleCompleteStep]);

  const handleHandlingUnitsContinue = useCallback(() => {
    if (!state.quotes?.length || state.isQuotesStale) {
      getQuotes();
    }
    handleCompleteStep('handling-units');
  }, [getQuotes, handleCompleteStep, state.quotes, state.isQuotesStale]);

  const handleQuotesContinue = useCallback(() => {
    handleCompleteStep('quotes');
  }, [handleCompleteStep]);

  const handleAdditionalDetailsSave = useCallback(() => {
    handleCompleteStep('additional-details');
  }, [handleCompleteStep]);

  const canBook = Boolean(
    completedSteps['ship-from'] &&
    completedSteps['ship-to'] &&
    completedSteps['handling-units'] &&
    completedSteps['quotes'] &&
    completedSteps['additional-details'] &&
    state.selectedQuote,
  );

  const isBookingConfirmation = drawerView === BOOKING_CONFIRMATION_VIEW;

  const renderDrawerContent = () => {
    if (isBookingConfirmation) {
      return (
        <>
          <ConfirmationNavBar onClose={onClose} />
          <ConfirmationBody />
        </>
      );
    }

    if (isBookingWizard) {
      return (
        <>
          <DrawerHeader
            closeButtonAriaLabel={freightQuoteModalMessages.closeAriaLabel}
            title={<FormattedMessage {...freightQuoteModalMessages.createShipmentTitle} />}
          />
          <div className={style['freight-drawer-body']}>
            <div className={style['freight-drawer-steps']}>
              <WizardStepComponent
                collapsedSummary={<ShipFromStepSummary />}
                isActive={activeStep === 'ship-from'}
                isCompleted={Boolean(completedSteps['ship-from'])}
                onEdit={() => handleEditStep('ship-from')}
                stepNumber={1}
                title='Ship From'
              >
                <ShipFromStep onSave={handleShipFromSave} />
              </WizardStepComponent>

              <WizardStepComponent
                collapsedSummary={<ShipToStepSummary />}
                isActive={activeStep === 'ship-to'}
                isCompleted={Boolean(completedSteps['ship-to'])}
                onEdit={() => handleEditStep('ship-to')}
                stepNumber={2}
                title='Ship To'
              >
                <ShipToStep onSave={handleShipToSave} />
              </WizardStepComponent>

              <WizardStepComponent
                collapsedSummary={<HandlingUnitsStepSummary />}
                isActive={activeStep === 'handling-units'}
                isCompleted={Boolean(completedSteps['handling-units'])}
                onEdit={() => handleEditStep('handling-units')}
                stepNumber={3}
                title='Handling Units'
              >
                <HandlingUnitsStep onContinue={handleHandlingUnitsContinue} />
              </WizardStepComponent>

              <WizardStepComponent
                collapsedSummary={<QuotesStepSummary />}
                isActive={activeStep === 'quotes'}
                isCompleted={Boolean(completedSteps['quotes'])}
                onEdit={() => handleEditStep('quotes')}
                stepNumber={4}
                title='Quotes'
              >
                <QuotesStep onContinue={handleQuotesContinue} />
              </WizardStepComponent>

              <WizardStepComponent
                collapsedSummary={<AdditionalDetailsStepSummary />}
                isActive={activeStep === 'additional-details'}
                isCompleted={Boolean(completedSteps['additional-details'])}
                onEdit={() => handleEditStep('additional-details')}
                stepNumber={5}
                title='Additional Details'
              >
                <AdditionalDetailsStep onSave={handleAdditionalDetailsSave} />
              </WizardStepComponent>
            </div>

            <div className={style['freight-drawer-summary']}>
              <ShipmentSummary />
            </div>
          </div>

          <div className={style['freight-drawer-footer']}>
            <Button onClick={onClose} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...freightQuoteModalMessages.cancelButton} />
            </Button>
            <FlexRow gap={Gap.Space8}>
              <Button isLoading={state.isSavingQuote} onClick={saveQuote} theme={THEME_CONSTANTS.secondary}>
                <FormattedMessage {...freightQuoteModalMessages.saveDraftButton} />
              </Button>
              <Button
                disabled={!canBook}
                isLoading={state.isBookingShipment}
                onClick={() => bookShipment()}
                theme={THEME_CONSTANTS.primary}
              >
                <FormattedMessage {...freightQuoteModalMessages.bookShipmentButton} />
              </Button>
            </FlexRow>
          </div>
        </>
      );
    }

    return (
      <>
        <DrawerHeader
          closeButtonAriaLabel={freightQuoteModalMessages.closeAriaLabel}
          title={<FormattedMessage {...freightQuoteModalMessages.title} />}
        />
        <div className={style['freight-drawer-body']}>
          <div className={style['freight-drawer-sidebar']}>
            <FreightConfigForm onGetQuotes={getQuotes} />
          </div>
          <div className={style['freight-drawer-content']}>
            <FreightQuoteResults />
          </div>
        </div>
      </>
    );
  };

  return (
    <Drawer canvasClassName={style['freight-drawer-canvas']} className={style['freight-drawer']} onCancel={onClose} onClose={onClose}>
      {renderDrawerContent()}
    </Drawer>
  );
};

export const FreightQuoteModal = ({ onClose, wizardOpenConfig, seed }: FreightQuoteModalProps) => {
  return (
    <FreightQuoteProvider seed={seed} wizardOpenConfig={wizardOpenConfig}>
      <FreightQuoteModalInner onClose={onClose} />
    </FreightQuoteProvider>
  );
};
